/**
 * StockSettingsModal — "quando dar baixa no estoque".
 *
 * Cada gatilho tem um interruptor (liga/desliga) e, quando ligado, um modo:
 * **Perguntar** (mostra o convite, comportamento histórico) ou **Automático**.
 *
 * Gatilho sem runtime aparece desabilitado com "Em breve" — mesmo padrão do
 * ME/EPP no onboarding fiscal (CLAUDE.md #17). Um interruptor que não faz nada
 * é pior do que interruptor nenhum: o usuário liga, confia, e o estoque não
 * volta. Hoje `UNIMPLEMENTED_TRIGGERS` está VAZIA (os cinco gatilhos agem), e o
 * ramo continua aqui para o próximo gatilho que for modelado antes de existir.
 */
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button3D } from '@/components/ui/button-3d';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useStockSettings } from '@/hooks/useStockSettings';
import {
  STOCK_TRIGGERS,
  UNIMPLEMENTED_TRIGGERS,
  type StockSettings,
  type StockTriggerKey,
  type StockTriggerMode,
} from '@/lib/stockSettings';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function StockSettingsModal({ open, onOpenChange }: Props) {
  const { settings, save } = useStockSettings();
  const [draft, setDraft] = useState<StockSettings>(settings);

  // Re-hidrata ao abrir: o modal pode ficar montado entre aberturas e mostraria
  // o rascunho antigo de uma edição abandonada.
  useEffect(() => {
    if (open) setDraft(settings);
  }, [open, settings]);

  const setTrigger = (key: StockTriggerKey, patch: Partial<{ enabled: boolean; mode: StockTriggerMode }>) =>
    setDraft((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const handleSave = async () => {
    try {
      await save.mutateAsync(draft);
      toast.success('Configurações de estoque salvas.');
      onOpenChange(false);
    } catch {
      // toast tratado no onError da mutation
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Quando dar baixa no estoque</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          Por padrão o app <strong>pergunta</strong> antes de mexer no estoque. A venda rápida
          continua dando baixa na hora — é venda de balcão, o produto já saiu.
        </p>

        <div className="space-y-4">
          {STOCK_TRIGGERS.map((trigger) => {
            const soon = UNIMPLEMENTED_TRIGGERS.includes(trigger.key);
            const config = draft[trigger.key];
            return (
              <div key={trigger.key} className="rounded-lg border p-3">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <Label htmlFor={`trigger-${trigger.key}`} className="text-sm font-medium">
                      {trigger.label}
                      {soon && (
                        <span className="ml-2 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">
                          Em breve
                        </span>
                      )}
                    </Label>
                    <p className="mt-0.5 text-xs text-muted-foreground">{trigger.description}</p>
                  </div>
                  <Switch
                    id={`trigger-${trigger.key}`}
                    checked={!soon && config.enabled}
                    disabled={soon}
                    onCheckedChange={(checked) => setTrigger(trigger.key, { enabled: checked })}
                  />
                </div>

                {!soon && config.enabled && (
                  <div className="mt-3 flex gap-2">
                    {(['ask', 'auto'] as const).map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => setTrigger(trigger.key, { mode })}
                        className={`flex-1 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                          config.mode === mode
                            ? 'bg-[#2f6fae] text-white'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {mode === 'ask' ? 'Sempre perguntar' : 'Automático'}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter>
          {/* Cancelar nunca desabilita junto com o confirmar (CLAUDE.md #41). */}
          <Button3D color="gray" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button3D>
          <Button3D onClick={handleSave} disabled={save.isPending}>
            {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Salvar
          </Button3D>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
